import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { CreditorService } from '../creditor.service';
import { ErrorModalService } from '../../modal/error.modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CreditorStoredFile } from '../../interfaces/creditorStoredFile.interface';

@Component({
    selector: 'app-creditor-published-files',
    templateUrl: './creditor-published-files.component.html',
    styleUrls: ['./creditor-published-files.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditorPublishedFilesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public isLoading = true;
  public files: CreditorStoredFile[] = [];
  public caseId = '';

  constructor(
    private creditorService: CreditorService,
    private spinner: NgxSpinnerService,
    private errorModalService: ErrorModalService,
    private translate: TranslateService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  public ngOnInit(): void {
    this.caseId = this.route.snapshot.paramMap.get('id') || '';
    this.loadFiles();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public navigateBack(): void {
    if (!this.caseId) {
      this.router.navigate(['/borgenar/start']);
      return;
    }

    this.router.navigate(['/borgenar/arende', this.caseId]);
  }

  public get skeletonItems(): number[] {
    return [1, 2, 3];
  }

  public getFileIcon(mimeType?: string, fileName?: string): string[] {
    const ext = fileName?.split('.').pop()?.toLowerCase() ?? '';
    if (mimeType?.includes('pdf') || ext === 'pdf') return ['fas', 'file-pdf'];
    if (mimeType?.includes('word') || ext === 'docx' || ext === 'doc') return ['fas', 'file-word'];
    if (mimeType?.includes('sheet') || ext === 'xlsx' || ext === 'xls') return ['fas', 'file-excel'];
    if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return ['fas', 'file-image'];
    return ['fas', 'file-alt'];
  }

  public getFileAccentColor(mimeType?: string, fileName?: string): string {
    const ext = fileName?.split('.').pop()?.toLowerCase() ?? '';
    if (mimeType?.includes('pdf') || ext === 'pdf') return '#dc2626';
    if (mimeType?.includes('word') || ext === 'docx' || ext === 'doc') return '#2563eb';
    if (mimeType?.includes('sheet') || ext === 'xlsx' || ext === 'xls') return '#16a34a';
    if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '#d97706';
    return '#7c3aed';
  }

  public formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  public downloadFile(file: CreditorStoredFile): void {
    this.creditorService.getPublishedFileStream(file.id, this.caseId, file.isSharepointFile, file.creditorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.fileName;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 10000);
        },
        error: () => {
          this.errorModalService.open(
            this.translate.instant('error.title'),
            this.translate.instant('published-files.download-error')
          );
        }
      });
  }

  private loadFiles(): void {
    this.isLoading = true;
    this.spinner.show();

    this.creditorService.getPublishedFiles(this.caseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (files) => {
          this.files = files;
          this.isLoading = false;
          this.spinner.hide();
        },
        error: () => {
          this.isLoading = false;
          this.spinner.hide();
          this.errorModalService.open(
            this.translate.instant('error.load-error-title'),
            this.translate.instant('published-files.load-error')
          );
        }
      });
  }
}
